/**
 * BATCH 22, PART 19 — Workflows 226-238 (Braze)
 * 13 cross-channel marketing workflows using Braze push, in-app, email, SMS, content cards
 * Services: Braze, Shopify, Stripe, OpenAI, Slack, Google Sheets
 */

// ============ HELPER FUNCTIONS ============

function n(
  id: string,
  type: string,
  label: string,
  y: number,
  data: any = {},
  x = 80,
): any {
  return {
    id,
    type,
    position: { x, y },
    data: { nodeLabel: label, description: label, ...data },
  };
}
function e(source: string, target: string, handle = "output_1"): any {
  return {
    id: `${source}->${target}`,
    type: "custom",
    source,
    target,
    sourceHandle: handle,
  };
}
function trigger(
  label: string,
  triggerMode: "webhook_app" | "webhook_form" | "schedule" | "manual" | "email",
  config: {
    source?: string;
    event?: string;
    schedule?: string;
    howItStarts: string;
    setup: string;
    inputFields: Array<{ name: string; type: string; example?: string }>;
  },
): any {
  return n("1", "campaign", label, 40, {
    triggerType:
      triggerMode === "schedule"
        ? "schedule"
        : triggerMode === "email"
          ? "email"
          : "webhook",
    triggerMode,
    triggerSource: config.source,
    triggerEvent: config.event,
    scheduleDescription: config.schedule,
    howItStarts: config.howItStarts,
    setupInstructions: config.setup,
    inputFields: config.inputFields,
    description: config.howItStarts,
  });
}
function aiNode(
  id: string,
  label: string,
  y: number,
  systemPrompt: string,
  userPrompt: string,
  outputs: Array<{ field: string; as: string; type: string }>,
  x = 80,
): any {
  return n(
    id,
    "hitApi",
    label,
    y,
    {
      hitApiDetails: {
        method: "POST",
        url: "https://api.openai.com/v1/chat/completions",
        headers: [
          { key: "Authorization", value: "Bearer {openai_key}" },
          { key: "Content-Type", value: "application/json" },
        ],
        body: [
          { key: "model", value: "gpt-4o-mini" },
          {
            key: "messages",
            value: `[{"role":"system","content":"${systemPrompt}"},{"role":"user","content":"${userPrompt}"}]`,
          },
          { key: "response_format", value: '{"type":"json_object"}' },
        ],
        variables: [
          ...outputs.map((o) => ({
            name: `choices[0].message.content.${o.field}`,
            customName: o.as,
            type: o.type,
          })),
          {
            name: "usage.total_tokens",
            customName: "tokens_used",
            type: "number",
          },
        ],
      },
    },
    x,
  );
}
function slackNode(
  id: string,
  label: string,
  y: number,
  message: string,
  x = 80,
): any {
  return n(
    id,
    "hitApi",
    label,
    y,
    {
      hitApiDetails: {
        method: "POST",
        url: "https://hooks.slack.com/services/YOUR_WEBHOOK_URL",
        headers: [{ key: "Content-Type", value: "application/json" }],
        body: [{ key: "text", value: message }],
        variables: [{ name: "ok", customName: "slack_sent", type: "boolean" }],
      },
    },
    x,
  );
}
function emailNode(
  id: string,
  label: string,
  y: number,
  to: string,
  subject: string,
  body: string,
  x = 80,
): any {
  return n(
    id,
    "sendEmail",
    label,
    y,
    { sendEmailDetails: { to, content: { subject, body } } },
    x,
  );
}
function smsNode(
  id: string,
  label: string,
  y: number,
  to: string,
  message: string,
  x = 80,
): any {
  return n(id, "sendSMS", label, y, { sendSmsDetails: { to, message } }, x);
}
function sheetsNode(
  id: string,
  label: string,
  y: number,
  sheetTab: string,
  values: string,
  x = 80,
): any {
  return n(
    id,
    "hitApi",
    label,
    y,
    {
      hitApiDetails: {
        method: "POST",
        url: `https://sheets.googleapis.com/v4/spreadsheets/{spreadsheet_id}/values/${sheetTab}!A1:append?valueInputOption=USER_ENTERED`,
        headers: [
          { key: "Authorization", value: "Bearer {google_token}" },
          { key: "Content-Type", value: "application/json" },
        ],
        body: [{ key: "values", value: values }],
        variables: [
          {
            name: "updates.updatedRows",
            customName: "rows_logged",
            type: "number",
          },
        ],
      },
    },
    x,
  );
}

// ============ BRAZE API HELPERS ============

function brazeApiNode(
  id: string,
  label: string,
  y: number,
  endpoint: string,
  bodyKeys: Array<{ key: string; value: string }>,
  variables: Array<{ name: string; customName: string; type: string }>,
  x = 80,
): any {
  return n(
    id,
    "hitApi",
    label,
    y,
    {
      hitApiDetails: {
        method: "POST",
        url: `https://rest.iad-01.braze.com${endpoint}`,
        headers: [
          { key: "Authorization", value: "Bearer {braze_api_key}" },
          { key: "Content-Type", value: "application/json" },
        ],
        body: bodyKeys,
        variables,
      },
    },
    x,
  );
}

function shopifyApiNode(
  id: string,
  label: string,
  y: number,
  endpoint: string,
  variables: Array<{ name: string; customName: string; type: string }>,
  x = 80,
): any {
  return n(
    id,
    "hitApi",
    label,
    y,
    {
      hitApiDetails: {
        method: "GET",
        url: `https://{shop_domain}.myshopify.com/admin/api/2024-01${endpoint}`,
        headers: [
          { key: "X-Shopify-Access-Token", value: "{shopify_token}" },
          { key: "Content-Type", value: "application/json" },
        ],
        body: [],
        variables,
      },
    },
    x,
  );
}

function stripeApiNode(
  id: string,
  label: string,
  y: number,
  endpoint: string,
  variables: Array<{ name: string; customName: string; type: string }>,
  x = 80,
): any {
  return n(
    id,
    "hitApi",
    label,
    y,
    {
      hitApiDetails: {
        method: "GET",
        url: `https://api.stripe.com/v1${endpoint}`,
        headers: [
          { key: "Authorization", value: "Bearer {stripe_secret_key}" },
          { key: "Content-Type", value: "application/x-www-form-urlencoded" },
        ],
        body: [],
        variables,
      },
    },
    x,
  );
}

// ============ WORKFLOWS 226-238 ============

export const ELITE_BATCH22_PART19: Record<
  string,
  { nodes: any[]; edges: any[]; summary: string }
> = {
  // ── 226. Back in Stock Journey | Pattern D ──
  "Back in Stock Journey": {
    nodes: [
      trigger("Braze: Back-in-stock webhook", "webhook_app", {
        source: "braze",
        event: "product.back_in_stock",
        howItStarts:
          "Fires when Shopify inventory is replenished for a previously out-of-stock product that users have expressed interest in via wishlists or back-in-stock alerts.",
        setup:
          "Connect Braze webhook to Shopify inventory_levels/update event. Filter for items transitioning from 0 to >0 inventory.",
        inputFields: [],
      }),
      aiNode(
        "2",
        "OpenAI: Extract product and waitlist details",
        280,
        "You are a product restock analyst. Extract the restocked product details and estimate demand urgency from the webhook payload. Return JSON with product_id, product_name, variant_title, current_stock, category, price, waitlist_count, urgency_level (high if waitlist>50 or stock<20, medium otherwise), and restock_message.",
        "Restock event payload: {trigger_body}",
        [
          { field: "product_id", as: "product_id", type: "string" },
          { field: "product_name", as: "product_name", type: "string" },
          { field: "current_stock", as: "current_stock", type: "number" },
          { field: "price", as: "price", type: "string" },
          { field: "waitlist_count", as: "waitlist_count", type: "number" },
          { field: "urgency_level", as: "urgency_level", type: "string" },
          { field: "restock_message", as: "restock_message", type: "string" },
        ],
      ),
      brazeApiNode(
        "3",
        "Braze: Lookup waitlisted users segment",
        520,
        "/segments/details",
        [{ key: "segment_id", value: "back_in_stock_{product_id}" }],
        [
          {
            name: "message_extras.segment_size",
            customName: "segment_size",
            type: "number",
          },
          { name: "name", customName: "segment_name", type: "string" },
        ],
      ),
      n("4", "condition", "High urgency restock?", 760, {
        conditionDetails: {
          conditions: [
            { field: "urgency_level", operator: "equals", value: "high" },
          ],
        },
      }),
      brazeApiNode(
        "5",
        "Braze: Send push + content card alert",
        1000,
        "/messages/send",
        [
          { key: "segment_id", value: "back_in_stock_{product_id}" },
          {
            key: "messages",
            value:
              '{"apple_push":{"alert":"{product_name} is back! Only {current_stock} left — grab yours now.","extra":{"product_id":"{product_id}"}},"android_push":{"alert":"{product_name} is back! Only {current_stock} left — grab yours now.","extra":{"product_id":"{product_id}"}},"content_card":{"title":"Back in Stock","description":"{restock_message}","url":"https://shop.example.com/products/{product_id}"}}',
          },
        ],
        [
          {
            name: "dispatch_id",
            customName: "push_dispatch_id",
            type: "string",
          },
          { name: "message", customName: "push_status", type: "string" },
        ],
      ),
      n("6", "delayAction", "Wait 24 hours for purchase", 1240, {
        delayDetails: { duration: 24, unit: "hours" },
      }),
      n("7", "condition", "Item still available and not purchased?", 1480, {
        conditionDetails: {
          conditions: [
            { field: "current_stock", operator: "greater_than", value: "0" },
          ],
        },
      }),
      emailNode(
        "8",
        "Braze: Send urgency email follow-up",
        1720,
        "{user_email}",
        "Last chance — {product_name} is selling fast!",
        "Hi {first_name},\n\n{product_name} came back in stock but quantities are limited. With only {current_stock} units remaining, this could sell out again soon.\n\n{restock_message}\n\nPrice: {price}\n\nDon't miss out this time — shop now before it's gone again.\n\n[Shop Now](https://shop.example.com/products/{product_id})",
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5", "output_1"),
      e("4", "6", "output_2"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8", "output_1"),
    ],
    summary:
      "Triggers when Shopify inventory is replenished for a previously out-of-stock item. AI analyzes restock urgency and waitlist size, then Braze sends push notifications and content cards to users who registered for back-in-stock alerts. After 24 hours, if the item remains available and the user has not purchased, a follow-up urgency email is sent to drive final conversion.",
  },

  // ── 227. Low Inventory Urgency Journey | Pattern D ──
  "Low Inventory Urgency Journey": {
    nodes: [
      trigger("Braze: Low inventory threshold webhook", "webhook_app", {
        source: "braze",
        event: "product.low_inventory",
        howItStarts:
          "Fires when Shopify product inventory drops below a configured threshold (e.g., <10 units) for items that users have recently browsed or wishlisted.",
        setup:
          "Connect Braze webhook to Shopify inventory threshold alerts. Filter for products with active browser or wishlist interest segments.",
        inputFields: [],
      }),
      aiNode(
        "2",
        "OpenAI: Analyze scarcity and interest signals",
        280,
        "You are a scarcity marketing specialist. Analyze the low-inventory event and determine the urgency tier and messaging strategy. Return JSON with product_id, product_name, remaining_stock, category, price, interest_segment_size, scarcity_tier (critical if stock<5, urgent if stock<15, moderate otherwise), scarcity_headline, and scarcity_body.",
        "Low inventory event: {trigger_body}",
        [
          { field: "product_id", as: "product_id", type: "string" },
          { field: "product_name", as: "product_name", type: "string" },
          { field: "remaining_stock", as: "remaining_stock", type: "number" },
          { field: "price", as: "price", type: "string" },
          { field: "scarcity_tier", as: "scarcity_tier", type: "string" },
          {
            field: "scarcity_headline",
            as: "scarcity_headline",
            type: "string",
          },
          { field: "scarcity_body", as: "scarcity_body", type: "string" },
        ],
      ),
      brazeApiNode(
        "3",
        "Braze: Fetch recently interested user segment",
        520,
        "/segments/details",
        [{ key: "segment_id", value: "recently_browsed_{product_id}" }],
        [
          {
            name: "message_extras.segment_size",
            customName: "interested_users",
            type: "number",
          },
          { name: "name", customName: "interest_segment_name", type: "string" },
        ],
      ),
      n("4", "condition", "Critical scarcity level?", 760, {
        conditionDetails: {
          conditions: [
            { field: "scarcity_tier", operator: "equals", value: "critical" },
          ],
        },
      }),
      brazeApiNode(
        "5",
        "Braze: Send push urgency alert with stock count",
        1000,
        "/messages/send",
        [
          { key: "segment_id", value: "recently_browsed_{product_id}" },
          {
            key: "messages",
            value:
              '{"apple_push":{"alert":"Only {remaining_stock} left! {product_name} is almost gone.","extra":{"product_id":"{product_id}","urgency":"critical"}},"android_push":{"alert":"Only {remaining_stock} left! {product_name} is almost gone.","extra":{"product_id":"{product_id}","urgency":"critical"}}}',
          },
        ],
        [
          {
            name: "dispatch_id",
            customName: "urgency_dispatch_id",
            type: "string",
          },
          {
            name: "message",
            customName: "urgency_push_status",
            type: "string",
          },
        ],
      ),
      n("6", "delayAction", "Wait 12 hours", 1240, {
        delayDetails: { duration: 12, unit: "hours" },
      }),
      n(
        "7",
        "condition",
        "Stock still available and user has not purchased?",
        1480,
        {
          conditionDetails: {
            conditions: [
              {
                field: "remaining_stock",
                operator: "greater_than",
                value: "0",
              },
            ],
          },
        },
      ),
      emailNode(
        "8",
        "Braze: Send last-chance email with alternatives",
        1720,
        "{user_email}",
        "{scarcity_headline}",
        "Hi {first_name},\n\n{scarcity_body}\n\nThere are only {remaining_stock} units of {product_name} left at {price}. Once they're gone, we can't guarantee a restock date.\n\n[Buy Now](https://shop.example.com/products/{product_id})\n\nIf this sells out, here are similar items you might love:\n- [Similar Item 1](https://shop.example.com/collections/{category})\n- [Similar Item 2](https://shop.example.com/collections/{category})\n\nDon't wait — scarcity is real this time.",
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5", "output_1"),
      e("4", "6", "output_2"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8", "output_1"),
    ],
    summary:
      "Monitors Shopify inventory drops below configured thresholds for products with active browser interest. AI classifies scarcity urgency tier and crafts messaging, then Braze sends push notifications to recently interested users. After 12 hours, users who have not purchased receive a last-chance email with stock count and similar product alternatives.",
  },

  // ── 228. Post-Purchase Cross-Sell Canvas | Pattern E ──
  "Post-Purchase Cross-Sell Canvas": {
    nodes: [
      trigger("Braze: Order placed webhook", "webhook_app", {
        source: "braze",
        event: "order.placed",
        howItStarts:
          "Fires when a customer completes a purchase in Shopify, capturing order details including products, total value, and customer profile for cross-sell targeting.",
        setup:
          "Connect Braze webhook to Shopify orders/create event. Ensure product metadata and customer attributes are forwarded.",
        inputFields: [],
      }),
      aiNode(
        "2",
        "OpenAI: Analyze order and generate cross-sell recommendations",
        280,
        "You are a cross-sell strategist for an e-commerce brand. Analyze the purchase and recommend 2-3 complementary products based on the order contents. Return JSON with order_id, customer_email, customer_name, ordered_products (comma-separated), order_total, is_first_purchase (boolean), cross_sell_products (array of {name, reason, url}), education_tip related to the purchased product, and cross_sell_subject_line.",
        "Order event: {trigger_body}",
        [
          { field: "order_id", as: "order_id", type: "string" },
          { field: "customer_email", as: "customer_email", type: "string" },
          { field: "customer_name", as: "customer_name", type: "string" },
          { field: "ordered_products", as: "ordered_products", type: "string" },
          { field: "order_total", as: "order_total", type: "string" },
          {
            field: "is_first_purchase",
            as: "is_first_purchase",
            type: "string",
          },
          { field: "education_tip", as: "education_tip", type: "string" },
          {
            field: "cross_sell_subject_line",
            as: "cross_sell_subject_line",
            type: "string",
          },
        ],
      ),
      brazeApiNode(
        "3",
        "Braze: Lookup customer profile and purchase history",
        520,
        "/users/export/ids",
        [
          { key: "email_address", value: "{customer_email}" },
          {
            key: "fields_to_export",
            value:
              '["external_id","custom_attributes","total_revenue","purchases"]',
          },
        ],
        [
          {
            name: "users[0].external_id",
            customName: "braze_user_id",
            type: "string",
          },
          {
            name: "users[0].total_revenue",
            customName: "lifetime_revenue",
            type: "number",
          },
          {
            name: "users[0].custom_attributes.vip_tier",
            customName: "vip_tier",
            type: "string",
          },
        ],
      ),
      n("4", "condition", "First purchase?", 760, {
        conditionDetails: {
          conditions: [
            { field: "is_first_purchase", operator: "equals", value: "true" },
          ],
        },
      }),
      emailNode(
        "5",
        "Braze: Send thank-you with product education",
        1000,
        "{customer_email}",
        "Welcome to the family! Here's how to get the most from your {ordered_products}",
        "Hi {customer_name},\n\nThank you for your first order (#{order_id})! We're thrilled to have you.\n\n**Getting the Most from Your Purchase:**\n{education_tip}\n\nYour order of {ordered_products} is being prepared and you'll receive tracking details shortly.\n\nWelcome aboard — we're here if you need anything!\n\nThe Team",
      ),
      n("6", "delayAction", "Wait 7 days for delivery and use", 1240, {
        delayDetails: { duration: 7, unit: "days" },
      }),
      n("7", "condition", "Customer lifetime value above $150?", 1480, {
        conditionDetails: {
          conditions: [
            {
              field: "lifetime_revenue",
              operator: "greater_than",
              value: "150",
            },
          ],
        },
      }),
      emailNode(
        "8",
        "Braze: Send personalized cross-sell recommendations",
        1720,
        "{customer_email}",
        "{cross_sell_subject_line}",
        "Hi {customer_name},\n\nWe hope you're loving your {ordered_products}! Based on your purchase, we handpicked a few items we think you'll enjoy:\n\nThese complement what you already have and our customers who bought similar items gave these top ratings.\n\nAs a valued customer with {vip_tier} status, you get priority access to our newest arrivals.\n\n[Browse Recommendations](https://shop.example.com/recommended?order={order_id})\n\nHappy shopping!",
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5", "output_1"),
      e("4", "6", "output_2"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8", "output_1"),
    ],
    summary:
      "Triggers on Shopify purchase, using AI to analyze order contents and generate complementary product recommendations. First-time buyers receive a welcome email with product education tips. After 7 days for delivery and use, high-LTV customers receive personalized cross-sell recommendations based on their purchase history and VIP tier.",
  },

  // ── 229. Review Request Journey | Pattern E ──
  "Review Request Journey": {
    nodes: [
      trigger("Braze: Order delivered webhook", "webhook_app", {
        source: "braze",
        event: "order.delivered",
        howItStarts:
          "Fires when Shopify marks an order as delivered, indicating the customer has received their purchase and is ready for a review request.",
        setup:
          "Connect Braze webhook to Shopify fulfillment events. Filter for delivery_status=delivered events only.",
        inputFields: [],
      }),
      aiNode(
        "2",
        "OpenAI: Extract delivery and product details",
        280,
        "You are a customer feedback specialist. Extract delivery details and craft a personalized review request. Return JSON with order_id, customer_email, customer_name, delivered_products (comma-separated), primary_product_name, delivery_days (number of days from order to delivery), satisfaction_likelihood (high if delivery_days<5, medium if <8, low otherwise), review_prompt (personalized question about the product), and review_subject.",
        "Delivery event: {trigger_body}",
        [
          { field: "order_id", as: "order_id", type: "string" },
          { field: "customer_email", as: "customer_email", type: "string" },
          { field: "customer_name", as: "customer_name", type: "string" },
          {
            field: "primary_product_name",
            as: "primary_product_name",
            type: "string",
          },
          {
            field: "delivered_products",
            as: "delivered_products",
            type: "string",
          },
          {
            field: "satisfaction_likelihood",
            as: "satisfaction_likelihood",
            type: "string",
          },
          { field: "review_prompt", as: "review_prompt", type: "string" },
          { field: "review_subject", as: "review_subject", type: "string" },
        ],
      ),
      brazeApiNode(
        "3",
        "Braze: Check customer tenure and past reviews",
        520,
        "/users/export/ids",
        [
          { key: "email_address", value: "{customer_email}" },
          {
            key: "fields_to_export",
            value: '["external_id","custom_attributes","created_at"]',
          },
        ],
        [
          {
            name: "users[0].external_id",
            customName: "braze_user_id",
            type: "string",
          },
          {
            name: "users[0].custom_attributes.reviews_submitted",
            customName: "past_reviews",
            type: "number",
          },
          {
            name: "users[0].custom_attributes.customer_since",
            customName: "customer_since",
            type: "string",
          },
        ],
      ),
      n("4", "condition", "First-time buyer?", 760, {
        conditionDetails: {
          conditions: [
            { field: "past_reviews", operator: "equals", value: "0" },
          ],
        },
      }),
      brazeApiNode(
        "5",
        "Braze: Send in-app review prompt with product image",
        1000,
        "/messages/send",
        [
          { key: "external_user_ids", value: '["{braze_user_id}"]' },
          {
            key: "messages",
            value:
              '{"in_app_message":{"type":"modal","header":"How are you liking your {primary_product_name}?","body":"{review_prompt}","buttons":[{"text":"Leave a Review","action":"uri","uri":"https://shop.example.com/review/{order_id}"},{"text":"Maybe Later","action":"none"}]}}',
          },
        ],
        [
          {
            name: "dispatch_id",
            customName: "inapp_dispatch_id",
            type: "string",
          },
          { name: "message", customName: "inapp_status", type: "string" },
        ],
      ),
      n("6", "delayAction", "Wait 5 days for review submission", 1240, {
        delayDetails: { duration: 5, unit: "days" },
      }),
      n("7", "condition", "Review not yet submitted?", 1480, {
        conditionDetails: {
          conditions: [
            { field: "past_reviews", operator: "equals", value: "0" },
          ],
        },
      }),
      emailNode(
        "8",
        "Braze: Send email review reminder with incentive",
        1720,
        "{customer_email}",
        "{review_subject}",
        "Hi {customer_name},\n\nYou've had your {primary_product_name} for a little while now — we'd love to hear what you think!\n\n{review_prompt}\n\nYour honest feedback helps other shoppers make confident decisions, and as a thank you, you'll earn 100 loyalty points for every review you submit.\n\n[Write Your Review](https://shop.example.com/review/{order_id})\n\nIt only takes a minute, and your voice matters.\n\nThank you!\nThe Team",
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5", "output_1"),
      e("4", "6", "output_2"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8", "output_1"),
    ],
    summary:
      "Triggers when Shopify marks an order as delivered. AI crafts a personalized review request based on the product and delivery experience. First-time buyers receive a Braze in-app message prompting a review with a direct link. After 5 days without a submission, a follow-up email offers loyalty points as an incentive to complete the review.",
  },

  // ── 230. Subscription Renewal Journey | Pattern F ──
  "Subscription Renewal Journey": {
    nodes: [
      trigger(
        "Braze: Subscription renewal approaching webhook",
        "webhook_app",
        {
          source: "braze",
          event: "subscription.renewal_approaching",
          howItStarts:
            "Fires 14 days before a Stripe subscription renewal date, giving the customer time to review their subscription and update payment details if needed.",
          setup:
            "Connect Braze webhook to Stripe subscription schedule events. Trigger when current_period_end is within 14 days.",
          inputFields: [],
        },
      ),
      aiNode(
        "2",
        "OpenAI: Analyze subscription and generate renewal brief",
        280,
        "You are a subscription retention analyst. Analyze the subscription details and craft a renewal communication strategy. Return JSON with subscription_id, customer_email, customer_name, plan_name, monthly_price, renewal_date, months_active, usage_summary (brief description of value delivered), renewal_risk (low if months_active>6, medium if 3-6, high if <3), value_headline, and value_bullets (3 key value points).",
        "Subscription renewal event: {trigger_body}",
        [
          { field: "subscription_id", as: "subscription_id", type: "string" },
          { field: "customer_email", as: "customer_email", type: "string" },
          { field: "customer_name", as: "customer_name", type: "string" },
          { field: "plan_name", as: "plan_name", type: "string" },
          { field: "monthly_price", as: "monthly_price", type: "string" },
          { field: "renewal_date", as: "renewal_date", type: "string" },
          { field: "renewal_risk", as: "renewal_risk", type: "string" },
          { field: "value_headline", as: "value_headline", type: "string" },
          { field: "value_bullets", as: "value_bullets", type: "string" },
        ],
      ),
      stripeApiNode(
        "3",
        "Stripe: Verify payment method and subscription status",
        520,
        "/subscriptions/{subscription_id}",
        [
          { name: "status", customName: "stripe_sub_status", type: "string" },
          {
            name: "default_payment_method",
            customName: "payment_method_id",
            type: "string",
          },
          {
            name: "current_period_end",
            customName: "period_end_timestamp",
            type: "number",
          },
        ],
      ),
      n(
        "4",
        "condition",
        "Payment method missing or subscription at risk?",
        760,
        {
          conditionDetails: {
            conditions: [
              { field: "renewal_risk", operator: "equals", value: "high" },
            ],
          },
        },
      ),
      emailNode(
        "5",
        "Braze: Send renewal reminder email with value summary",
        1000,
        "{customer_email}",
        "Your {plan_name} renews on {renewal_date} — here's what you've achieved",
        "Hi {customer_name},\n\nYour {plan_name} subscription ({monthly_price}/mo) renews on {renewal_date}.\n\n**{value_headline}**\n\n{value_bullets}\n\nEverything is set for automatic renewal. If you'd like to update your payment method or make changes, you can do so anytime:\n\n[Manage Subscription](https://app.example.com/account/subscription)\n\nWe're glad to have you with us!\n\nThe Team",
      ),
      n("6", "delayAction", "Wait 10 days until 4 days before renewal", 1240, {
        delayDetails: { duration: 10, unit: "days" },
      }),
      n(
        "7",
        "condition",
        "Subscription still active and not cancelled?",
        1480,
        {
          conditionDetails: {
            conditions: [
              {
                field: "stripe_sub_status",
                operator: "equals",
                value: "active",
              },
            ],
          },
        },
      ),
      brazeApiNode(
        "8",
        "Braze: Send push confirmation and content card",
        1720,
        "/messages/send",
        [
          { key: "external_user_ids", value: '["{braze_user_id}"]' },
          {
            key: "messages",
            value:
              '{"apple_push":{"alert":"Your {plan_name} renews in 4 days. All set! Tap to review your account.","extra":{"action":"manage_subscription"}},"android_push":{"alert":"Your {plan_name} renews in 4 days. All set! Tap to review your account.","extra":{"action":"manage_subscription"}},"content_card":{"title":"Renewal Coming Up","description":"Your {plan_name} ({monthly_price}/mo) renews on {renewal_date}. Review or manage your subscription.","url":"https://app.example.com/account/subscription"}}',
          },
        ],
        [
          {
            name: "dispatch_id",
            customName: "renewal_push_dispatch",
            type: "string",
          },
          {
            name: "message",
            customName: "renewal_push_status",
            type: "string",
          },
        ],
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5", "output_1"),
      e("4", "6", "output_2"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8", "output_1"),
    ],
    summary:
      "Fires 14 days before a Stripe subscription renewal. AI analyzes account tenure and usage to assess renewal risk and craft value messaging. Stripe verifies payment method status. At-risk subscribers receive a renewal email highlighting delivered value. Four days before renewal, active subscribers get a push notification and content card confirming the upcoming charge with a link to manage their account.",
  },

  // ── 231. Payment Failed Dunning Journey | Pattern F ──
  "Payment Failed Dunning Journey": {
    nodes: [
      trigger("Braze: Payment failed webhook", "webhook_app", {
        source: "braze",
        event: "invoice.payment_failed",
        howItStarts:
          "Fires when Stripe reports a failed payment for a subscription invoice, initiating the dunning sequence to recover the payment before the subscription is cancelled.",
        setup:
          "Connect Braze webhook to Stripe invoice.payment_failed events. Include invoice, subscription, and customer metadata in payload.",
        inputFields: [],
      }),
      aiNode(
        "2",
        "OpenAI: Analyze failure and draft dunning communications",
        280,
        "You are a payment recovery specialist. Analyze the payment failure details and craft empathetic dunning messages. Return JSON with invoice_id, subscription_id, customer_email, customer_name, plan_name, amount_due, failure_reason (card_declined, insufficient_funds, expired_card, etc.), attempt_count, grace_period_end, dunning_subject, dunning_body_friendly (empathetic first attempt message), and dunning_body_urgent (final warning message).",
        "Payment failure event: {trigger_body}",
        [
          { field: "invoice_id", as: "invoice_id", type: "string" },
          { field: "subscription_id", as: "subscription_id", type: "string" },
          { field: "customer_email", as: "customer_email", type: "string" },
          { field: "customer_name", as: "customer_name", type: "string" },
          { field: "plan_name", as: "plan_name", type: "string" },
          { field: "amount_due", as: "amount_due", type: "string" },
          { field: "failure_reason", as: "failure_reason", type: "string" },
          { field: "attempt_count", as: "attempt_count", type: "number" },
          { field: "grace_period_end", as: "grace_period_end", type: "string" },
          { field: "dunning_subject", as: "dunning_subject", type: "string" },
          {
            field: "dunning_body_urgent",
            as: "dunning_body_urgent",
            type: "string",
          },
        ],
      ),
      stripeApiNode(
        "3",
        "Stripe: Check current invoice and retry status",
        520,
        "/invoices/{invoice_id}",
        [
          { name: "status", customName: "invoice_status", type: "string" },
          {
            name: "attempt_count",
            customName: "stripe_attempts",
            type: "number",
          },
          {
            name: "next_payment_attempt",
            customName: "next_retry_timestamp",
            type: "number",
          },
        ],
      ),
      n("4", "condition", "First failure attempt?", 760, {
        conditionDetails: {
          conditions: [
            { field: "attempt_count", operator: "equals", value: "1" },
          ],
        },
      }),
      emailNode(
        "5",
        "Braze: Send friendly payment update request",
        1000,
        "{customer_email}",
        "{dunning_subject}",
        "Hi {customer_name},\n\nWe had trouble processing your {amount_due} payment for {plan_name}. This can happen for a number of reasons — most commonly an expired card or temporary bank hold.\n\nTo keep your subscription active without interruption, please update your payment method:\n\n[Update Payment Method](https://app.example.com/account/billing)\n\nYour access continues during the grace period (until {grace_period_end}). If you've already updated your card, you can disregard this message — we'll retry automatically.\n\nNeed help? Just reply to this email.\n\nThe Team",
      ),
      n("6", "delayAction", "Wait 3 days for payment resolution", 1240, {
        delayDetails: { duration: 3, unit: "days" },
      }),
      n("7", "condition", "Payment still unresolved?", 1480, {
        conditionDetails: {
          conditions: [
            { field: "invoice_status", operator: "equals", value: "open" },
          ],
        },
      }),
      brazeApiNode(
        "8",
        "Braze: Send push + SMS urgent dunning with deadline",
        1720,
        "/messages/send",
        [
          { key: "external_user_ids", value: '["{braze_user_id}"]' },
          {
            key: "messages",
            value:
              '{"apple_push":{"alert":"Action needed: Your {plan_name} subscription will be paused if payment isn\'t updated by {grace_period_end}.","extra":{"action":"update_payment"}},"android_push":{"alert":"Action needed: Your {plan_name} subscription will be paused if payment isn\'t updated by {grace_period_end}.","extra":{"action":"update_payment"}},"sms":{"body":"Hi {customer_name}, your {plan_name} payment of {amount_due} needs attention. Update by {grace_period_end} to avoid service interruption: https://app.example.com/account/billing"}}',
          },
        ],
        [
          {
            name: "dispatch_id",
            customName: "dunning_dispatch_id",
            type: "string",
          },
          {
            name: "message",
            customName: "dunning_escalation_status",
            type: "string",
          },
        ],
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5", "output_1"),
      e("4", "6", "output_2"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8", "output_1"),
    ],
    summary:
      "Initiates when Stripe reports a failed subscription payment. AI analyzes the failure reason and attempt count to craft appropriate dunning messages. First-time failures receive a friendly email explaining the issue with a direct link to update payment. After 3 days without resolution, Braze escalates with push notifications and SMS containing the grace period deadline to prevent subscription cancellation.",
  },

  // ── 232. Winback Reactivation | Pattern G ──
  "Winback Reactivation": {
    nodes: [
      trigger("Braze: 90-day inactivity schedule trigger", "schedule", {
        schedule: "Daily at 9:00 AM UTC",
        howItStarts:
          "Runs daily to identify users who have not opened the app, clicked an email, or made a purchase in the last 90 days, triggering a multi-channel winback sequence.",
        setup:
          "Configure Braze scheduled campaign to query users where last_active > 90 days ago. Exclude users who have unsubscribed or been recently contacted by another winback campaign.",
        inputFields: [],
      }),
      brazeApiNode(
        "2",
        "Braze: Fetch inactive user profile and purchase history",
        280,
        "/users/export/segment",
        [
          { key: "segment_id", value: "inactive_90_days" },
          {
            key: "fields_to_export",
            value:
              '["external_id","email","first_name","custom_attributes","total_revenue","last_active"]',
          },
        ],
        [
          {
            name: "users[0].external_id",
            customName: "braze_user_id",
            type: "string",
          },
          { name: "users[0].email", customName: "user_email", type: "string" },
          {
            name: "users[0].first_name",
            customName: "first_name",
            type: "string",
          },
          {
            name: "users[0].total_revenue",
            customName: "lifetime_revenue",
            type: "number",
          },
          {
            name: "users[0].last_active",
            customName: "last_active_date",
            type: "string",
          },
        ],
      ),
      aiNode(
        "3",
        "OpenAI: Analyze inactivity reason and recommend winback strategy",
        520,
        "You are a customer reactivation strategist. Analyze the inactive user profile and recommend a winback approach. Return JSON with inactivity_days, likely_reason (lost_interest, found_competitor, seasonal, life_change), winback_angle (new_features, personalized_offer, miss_you, whats_new), winback_subject, winback_headline, winback_body, offer_recommendation (percentage discount or free month), and is_high_value (true if lifetime_revenue > 200).",
        "Inactive user: email={user_email}, last_active={last_active_date}, lifetime_revenue={lifetime_revenue}",
        [
          { field: "likely_reason", as: "likely_reason", type: "string" },
          { field: "winback_angle", as: "winback_angle", type: "string" },
          { field: "winback_subject", as: "winback_subject", type: "string" },
          { field: "winback_headline", as: "winback_headline", type: "string" },
          { field: "winback_body", as: "winback_body", type: "string" },
          {
            field: "offer_recommendation",
            as: "offer_recommendation",
            type: "string",
          },
          { field: "is_high_value", as: "is_high_value", type: "string" },
        ],
      ),
      n("4", "condition", "High-value customer?", 760, {
        conditionDetails: {
          conditions: [
            { field: "is_high_value", operator: "equals", value: "true" },
          ],
        },
      }),
      emailNode(
        "5",
        "Braze: Send personalized winback email",
        1000,
        "{user_email}",
        "{winback_subject}",
        "Hi {first_name},\n\n{winback_headline}\n\n{winback_body}\n\nWe've been busy making things better since your last visit. Here's a taste of what's new:\n\n- Redesigned dashboard with faster insights\n- New integrations you requested\n- Improved mobile experience\n\nAs a thank you for being a loyal member, here's a special offer: {offer_recommendation}\n\n[Come Back & Explore](https://app.example.com/welcome-back?user={braze_user_id})\n\nWe'd love to have you back.\n\nThe Team",
      ),
      n("6", "delayAction", "Wait 10 days for re-engagement", 1240, {
        delayDetails: { duration: 10, unit: "days" },
      }),
      n("7", "condition", "Still inactive after first winback?", 1480, {
        conditionDetails: {
          conditions: [
            {
              field: "last_active_date",
              operator: "not_equals",
              value: "today",
            },
          ],
        },
      }),
      brazeApiNode(
        "8",
        "Braze: Send push + content card with stronger offer or sunset",
        1720,
        "/messages/send",
        [
          { key: "external_user_ids", value: '["{braze_user_id}"]' },
          {
            key: "messages",
            value:
              '{"apple_push":{"alert":"We miss you, {first_name}! Your exclusive {offer_recommendation} offer expires soon. Tap to claim.","extra":{"action":"winback_offer"}},"android_push":{"alert":"We miss you, {first_name}! Your exclusive {offer_recommendation} offer expires soon. Tap to claim.","extra":{"action":"winback_offer"}},"content_card":{"title":"Welcome Back Offer","description":"Claim your {offer_recommendation} before it expires. We have exciting new features waiting for you.","url":"https://app.example.com/welcome-back?user={braze_user_id}"}}',
          },
        ],
        [
          {
            name: "dispatch_id",
            customName: "winback_final_dispatch",
            type: "string",
          },
          {
            name: "message",
            customName: "winback_final_status",
            type: "string",
          },
        ],
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5", "output_1"),
      e("4", "6", "output_2"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8", "output_1"),
    ],
    summary:
      "Runs daily to identify 90-day inactive users via Braze segments. AI analyzes each user's profile and lifetime value to determine the likely inactivity reason and recommend a winback strategy. High-value customers receive a personalized email with new features and a tailored offer. After 10 days of continued inactivity, a final push notification and content card deliver a stronger incentive before potential sunset.",
  },

  // ── 233. Predictive Churn Prevention | Pattern L ──
  "Predictive Churn Prevention": {
    nodes: [
      trigger("Braze: Churn risk score webhook", "webhook_app", {
        source: "braze",
        event: "user.churn_risk_high",
        howItStarts:
          "Fires when Braze's predictive churn model flags a user with a high churn probability score (>0.7), indicating they are likely to stop using the product within 30 days.",
        setup:
          "Enable Braze Predictive Churn and configure a webhook trigger for users entering the high-risk segment. Include churn score, risk factors, and user attributes in the payload.",
        inputFields: [],
      }),
      brazeApiNode(
        "2",
        "Braze: Fetch at-risk user profile and engagement history",
        280,
        "/users/export/ids",
        [
          { key: "email_address", value: "{user_email}" },
          {
            key: "fields_to_export",
            value:
              '["external_id","email","first_name","custom_attributes","total_revenue","last_active","campaigns_received"]',
          },
        ],
        [
          {
            name: "users[0].external_id",
            customName: "braze_user_id",
            type: "string",
          },
          { name: "users[0].email", customName: "user_email", type: "string" },
          {
            name: "users[0].first_name",
            customName: "first_name",
            type: "string",
          },
          {
            name: "users[0].total_revenue",
            customName: "lifetime_revenue",
            type: "number",
          },
          {
            name: "users[0].custom_attributes.plan_name",
            customName: "plan_name",
            type: "string",
          },
          {
            name: "users[0].custom_attributes.usage_score",
            customName: "usage_score",
            type: "number",
          },
        ],
      ),
      aiNode(
        "3",
        "OpenAI: Analyze churn signals and recommend save action",
        520,
        "You are a customer retention strategist. Analyze the at-risk user profile and recommend an intervention. Return JSON with churn_probability, primary_risk_factors (comma-separated), recommended_action (proactive_checkin, feature_education, offer_discount, csm_escalation), save_email_subject, save_email_body (personalized retention message), internal_alert (summary for CS team), and escalation_needed (true if lifetime_revenue>500 or churn_probability>0.85).",
        "Churn risk event: {trigger_body}, user profile: email={user_email}, plan={plan_name}, usage_score={usage_score}, lifetime_revenue={lifetime_revenue}",
        [
          {
            field: "churn_probability",
            as: "churn_probability",
            type: "string",
          },
          {
            field: "primary_risk_factors",
            as: "primary_risk_factors",
            type: "string",
          },
          {
            field: "recommended_action",
            as: "recommended_action",
            type: "string",
          },
          {
            field: "save_email_subject",
            as: "save_email_subject",
            type: "string",
          },
          { field: "save_email_body", as: "save_email_body", type: "string" },
          { field: "internal_alert", as: "internal_alert", type: "string" },
          {
            field: "escalation_needed",
            as: "escalation_needed",
            type: "string",
          },
        ],
      ),
      n("4", "condition", "Expansion opportunity or save-risk?", 760, {
        conditionDetails: {
          conditions: [
            { field: "escalation_needed", operator: "equals", value: "true" },
          ],
        },
      }),
      slackNode(
        "5",
        "Slack: Alert CS team about high-value churn risk",
        1000,
        "🚨 *High-Value Churn Risk Alert*\n\n*User:* {first_name} ({user_email})\n*Plan:* {plan_name} | *LTV:* ${lifetime_revenue}\n*Churn Probability:* {churn_probability}\n*Risk Factors:* {primary_risk_factors}\n*Recommended Action:* {recommended_action}\n\n{internal_alert}\n\n<https://app.example.com/admin/users/{braze_user_id}|View User Profile>",
      ),
      n("6", "delayAction", "Wait 5 days for intervention effect", 1240, {
        delayDetails: { duration: 5, unit: "days" },
      }),
      n("7", "condition", "Usage score still declining?", 1480, {
        conditionDetails: {
          conditions: [
            { field: "usage_score", operator: "less_than", value: "30" },
          ],
        },
      }),
      emailNode(
        "8",
        "Braze: Send personalized save email with value reminder",
        1720,
        "{user_email}",
        "{save_email_subject}",
        "Hi {first_name},\n\n{save_email_body}\n\nWe noticed your usage has changed recently, and we want to make sure you're getting everything you need from {plan_name}.\n\nHere are a few things that might help:\n- Schedule a 15-minute strategy call with our team\n- Explore features you haven't tried yet\n- Review your account settings for optimization tips\n\n[Book a Strategy Call](https://app.example.com/support/schedule)\n[Explore Features](https://app.example.com/features)\n\nWe're here to help you succeed.\n\nThe Team",
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5", "output_1"),
      e("4", "6", "output_2"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8", "output_1"),
    ],
    summary:
      "Triggers when Braze's predictive churn model flags a high-risk user. AI analyzes the user's engagement history, usage patterns, and lifetime value to determine root causes and recommend a save strategy. High-value accounts trigger an immediate Slack alert to the CS team. After 5 days, if usage continues to decline, a personalized retention email offers strategy calls and feature education to prevent cancellation.",
  },

  // ── 234. Trial to Paid Conversion Journey | Pattern L ──
  "Trial to Paid Conversion Journey": {
    nodes: [
      trigger("Braze: Trial started webhook", "webhook_app", {
        source: "braze",
        event: "subscription.trial_started",
        howItStarts:
          "Fires when a new user begins a free trial via Stripe, capturing their trial plan, duration, and initial engagement signals for a conversion nurture sequence.",
        setup:
          "Connect Braze webhook to Stripe customer.subscription.trial_will_end and trial start events. Include plan details, trial length, and user attributes.",
        inputFields: [],
      }),
      brazeApiNode(
        "2",
        "Braze: Fetch trial user profile and activation status",
        280,
        "/users/export/ids",
        [
          { key: "email_address", value: "{user_email}" },
          {
            key: "fields_to_export",
            value:
              '["external_id","email","first_name","custom_attributes","last_active"]',
          },
        ],
        [
          {
            name: "users[0].external_id",
            customName: "braze_user_id",
            type: "string",
          },
          { name: "users[0].email", customName: "user_email", type: "string" },
          {
            name: "users[0].first_name",
            customName: "first_name",
            type: "string",
          },
          {
            name: "users[0].custom_attributes.features_activated",
            customName: "features_activated",
            type: "number",
          },
          {
            name: "users[0].custom_attributes.trial_plan",
            customName: "trial_plan",
            type: "string",
          },
          {
            name: "users[0].custom_attributes.trial_end_date",
            customName: "trial_end_date",
            type: "string",
          },
        ],
      ),
      aiNode(
        "3",
        "OpenAI: Assess trial engagement and recommend upgrade nudge",
        520,
        "You are a SaaS trial conversion strategist. Analyze the trial user's activation progress and recommend the best conversion approach. Return JSON with trial_days_remaining, activation_percentage (features_activated out of 5 key features), engagement_level (high if activation>60%, medium if 30-60%, low if <30%), conversion_approach (value_demo, feature_highlight, social_proof, urgency), upgrade_email_subject, upgrade_email_body, and upgrade_push_message.",
        "Trial user: email={user_email}, plan={trial_plan}, features_activated={features_activated}, trial_end={trial_end_date}",
        [
          {
            field: "trial_days_remaining",
            as: "trial_days_remaining",
            type: "number",
          },
          {
            field: "activation_percentage",
            as: "activation_percentage",
            type: "string",
          },
          { field: "engagement_level", as: "engagement_level", type: "string" },
          {
            field: "conversion_approach",
            as: "conversion_approach",
            type: "string",
          },
          {
            field: "upgrade_email_subject",
            as: "upgrade_email_subject",
            type: "string",
          },
          {
            field: "upgrade_email_body",
            as: "upgrade_email_body",
            type: "string",
          },
          {
            field: "upgrade_push_message",
            as: "upgrade_push_message",
            type: "string",
          },
        ],
      ),
      n("4", "condition", "High engagement — expansion opportunity?", 760, {
        conditionDetails: {
          conditions: [
            { field: "engagement_level", operator: "equals", value: "high" },
          ],
        },
      }),
      brazeApiNode(
        "5",
        "Braze: Send in-app upgrade prompt with feature unlock preview",
        1000,
        "/messages/send",
        [
          { key: "external_user_ids", value: '["{braze_user_id}"]' },
          {
            key: "messages",
            value:
              '{"in_app_message":{"type":"slideup","message":"{upgrade_push_message}","buttons":[{"text":"Upgrade Now","action":"uri","uri":"https://app.example.com/billing/upgrade?plan={trial_plan}"},{"text":"Learn More","action":"uri","uri":"https://app.example.com/pricing"}]}}',
          },
        ],
        [
          {
            name: "dispatch_id",
            customName: "inapp_upgrade_dispatch",
            type: "string",
          },
          {
            name: "message",
            customName: "inapp_upgrade_status",
            type: "string",
          },
        ],
      ),
      n("6", "delayAction", "Wait 5 days toward trial end", 1240, {
        delayDetails: { duration: 5, unit: "days" },
      }),
      n("7", "condition", "Trial ending soon and not yet converted?", 1480, {
        conditionDetails: {
          conditions: [
            {
              field: "trial_days_remaining",
              operator: "less_than",
              value: "4",
            },
          ],
        },
      }),
      emailNode(
        "8",
        "Braze: Send conversion email with urgency and social proof",
        1720,
        "{user_email}",
        "{upgrade_email_subject}",
        "Hi {first_name},\n\n{upgrade_email_body}\n\nYour {trial_plan} trial ends in {trial_days_remaining} days. You've already activated {activation_percentage} of the platform's key features — that's a great start.\n\nHere's what upgrading unlocks:\n- Unlimited access to all features\n- Priority support and onboarding assistance\n- Advanced analytics and reporting\n\nJoin 10,000+ teams who upgraded from trial to full access:\n\n\"We saw a 3x improvement in our workflow efficiency within the first month.\" — Marketing Director, Series B startup\n\n[Upgrade Now](https://app.example.com/billing/upgrade?plan={trial_plan})\n\nQuestions? Book a quick call with our team: [Schedule Demo](https://app.example.com/support/schedule)\n\nThe Team",
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5", "output_1"),
      e("4", "6", "output_2"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8", "output_1"),
    ],
    summary:
      "Triggers when a user starts a Stripe free trial. Braze fetches activation metrics while AI assesses engagement level and recommends a conversion approach. Highly engaged users receive an in-app upgrade prompt previewing premium features. As the trial end approaches, users who haven't converted receive an urgency email with social proof, feature highlights, and a direct upgrade link.",
  },

  // ── 235. Feature Adoption Milestone Nudge | Pattern A ──
  "Feature Adoption Milestone Nudge": {
    nodes: [
      trigger("Braze: Feature milestone event webhook", "webhook_app", {
        source: "braze",
        event: "user.feature_milestone",
        howItStarts:
          "Fires when a user completes a key feature milestone (e.g., first campaign sent, first integration connected, 10th workflow created), triggering a celebration and next-feature nudge.",
        setup:
          "Configure Braze custom events for feature milestones. Set up webhook trigger for events matching feature_milestone with milestone_name and feature_id attributes.",
        inputFields: [],
      }),
      aiNode(
        "2",
        "OpenAI: Identify milestone context and suggest next feature",
        280,
        "You are a product adoption coach. Analyze the feature milestone and recommend the next logical feature for the user to try. Return JSON with milestone_name, feature_completed, milestone_number (ordinal), celebration_message, next_feature_name, next_feature_benefit, next_feature_url_path, adoption_score (percentage of key features used), and push_congrats_message.",
        "Feature milestone event: {trigger_body}",
        [
          { field: "milestone_name", as: "milestone_name", type: "string" },
          {
            field: "feature_completed",
            as: "feature_completed",
            type: "string",
          },
          {
            field: "celebration_message",
            as: "celebration_message",
            type: "string",
          },
          {
            field: "next_feature_name",
            as: "next_feature_name",
            type: "string",
          },
          {
            field: "next_feature_benefit",
            as: "next_feature_benefit",
            type: "string",
          },
          {
            field: "next_feature_url_path",
            as: "next_feature_url_path",
            type: "string",
          },
          { field: "adoption_score", as: "adoption_score", type: "string" },
          {
            field: "push_congrats_message",
            as: "push_congrats_message",
            type: "string",
          },
        ],
      ),
      brazeApiNode(
        "3",
        "Braze: Fetch user profile and adoption progress",
        520,
        "/users/export/ids",
        [
          { key: "email_address", value: "{user_email}" },
          {
            key: "fields_to_export",
            value: '["external_id","email","first_name","custom_attributes"]',
          },
        ],
        [
          {
            name: "users[0].external_id",
            customName: "braze_user_id",
            type: "string",
          },
          { name: "users[0].email", customName: "user_email", type: "string" },
          {
            name: "users[0].first_name",
            customName: "first_name",
            type: "string",
          },
          {
            name: "users[0].custom_attributes.onboarding_complete",
            customName: "onboarding_complete",
            type: "string",
          },
        ],
      ),
      n("4", "condition", "Is user still in onboarding phase?", 760, {
        conditionDetails: {
          conditions: [
            {
              field: "onboarding_complete",
              operator: "equals",
              value: "false",
            },
          ],
        },
      }),
      brazeApiNode(
        "5",
        "Braze: Send push congratulation with next-feature nudge",
        1000,
        "/messages/send",
        [
          { key: "external_user_ids", value: '["{braze_user_id}"]' },
          {
            key: "messages",
            value:
              '{"apple_push":{"alert":"{push_congrats_message}","extra":{"action":"feature_milestone","next_feature":"{next_feature_url_path}"}},"android_push":{"alert":"{push_congrats_message}","extra":{"action":"feature_milestone","next_feature":"{next_feature_url_path}"}},"content_card":{"title":"Milestone Unlocked!","description":"{celebration_message} Ready for your next step? Try {next_feature_name}.","url":"https://app.example.com{next_feature_url_path}"}}',
          },
        ],
        [
          {
            name: "dispatch_id",
            customName: "milestone_dispatch_id",
            type: "string",
          },
          {
            name: "message",
            customName: "milestone_push_status",
            type: "string",
          },
        ],
      ),
      n(
        "6",
        "delayAction",
        "Wait 48 hours for next feature exploration",
        1240,
        {
          delayDetails: { duration: 48, unit: "hours" },
        },
      ),
      n("7", "condition", "User has not tried next feature yet?", 1480, {
        conditionDetails: {
          conditions: [
            { field: "adoption_score", operator: "less_than", value: "80" },
          ],
        },
      }),
      emailNode(
        "8",
        "Braze: Send feature education email with tutorial",
        1720,
        "{user_email}",
        "Nice work on {feature_completed}! Here's your next power move",
        "Hi {first_name},\n\n{celebration_message}\n\nYour adoption score is now {adoption_score} — impressive progress! Here's what we recommend trying next:\n\n**{next_feature_name}**\n{next_feature_benefit}\n\nThis feature pairs perfectly with {feature_completed} and most users see results within their first session.\n\n[Try {next_feature_name} Now](https://app.example.com{next_feature_url_path})\n\nWant a quick walkthrough? Check out our 3-minute tutorial:\n[Watch Tutorial](https://app.example.com/tutorials/{next_feature_name})\n\nKeep up the great work!\n\nThe Team",
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5", "output_1"),
      e("4", "6", "output_2"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8", "output_1"),
    ],
    summary:
      "Triggers when a user hits a feature adoption milestone in Braze. AI identifies the completed milestone context and recommends the next logical feature. Users still in onboarding receive an immediate push notification and content card celebrating the achievement. After 48 hours, users who haven't tried the recommended feature get an educational email with tutorials and benefit highlights.",
  },

  // ── 236. Push Primer and Permission Flow | Pattern A ──
  "Push Primer and Permission Flow": {
    nodes: [
      trigger("Braze: First app open webhook", "webhook_app", {
        source: "braze",
        event: "session.first_open",
        howItStarts:
          "Fires on a user's first app open, initiating a soft push primer flow that educates the user on notification value before requesting system-level push permission.",
        setup:
          "Configure Braze to track first_open session events. Filter for users where push_enabled is false or null. Exclude users who have previously dismissed the primer.",
        inputFields: [],
      }),
      aiNode(
        "2",
        "OpenAI: Determine optimal primer messaging based on user context",
        280,
        "You are a mobile engagement strategist. Based on the user's signup source and profile, craft a compelling push permission primer message. Return JSON with user_segment (new_user, returning, referral), primer_headline, primer_body (2-3 sentences explaining push value), primer_cta, value_propositions (3 bullet points of what notifications deliver), delay_minutes_before_prompt (15 for new users, 5 for returning), and fallback_email_subject.",
        "First open event: {trigger_body}",
        [
          { field: "user_segment", as: "user_segment", type: "string" },
          { field: "primer_headline", as: "primer_headline", type: "string" },
          { field: "primer_body", as: "primer_body", type: "string" },
          { field: "primer_cta", as: "primer_cta", type: "string" },
          {
            field: "value_propositions",
            as: "value_propositions",
            type: "string",
          },
          {
            field: "delay_minutes_before_prompt",
            as: "delay_minutes",
            type: "number",
          },
          {
            field: "fallback_email_subject",
            as: "fallback_email_subject",
            type: "string",
          },
        ],
      ),
      brazeApiNode(
        "3",
        "Braze: Check push permission status and device info",
        520,
        "/users/export/ids",
        [
          { key: "email_address", value: "{user_email}" },
          {
            key: "fields_to_export",
            value:
              '["external_id","email","first_name","push_tokens","custom_attributes"]',
          },
        ],
        [
          {
            name: "users[0].external_id",
            customName: "braze_user_id",
            type: "string",
          },
          { name: "users[0].email", customName: "user_email", type: "string" },
          {
            name: "users[0].first_name",
            customName: "first_name",
            type: "string",
          },
          {
            name: "users[0].push_tokens",
            customName: "existing_push_tokens",
            type: "string",
          },
        ],
      ),
      n("4", "condition", "User already has push enabled?", 760, {
        conditionDetails: {
          conditions: [
            { field: "existing_push_tokens", operator: "equals", value: "[]" },
          ],
        },
      }),
      brazeApiNode(
        "5",
        "Braze: Show in-app push primer message",
        1000,
        "/messages/send",
        [
          { key: "external_user_ids", value: '["{braze_user_id}"]' },
          {
            key: "messages",
            value:
              '{"in_app_message":{"type":"modal","header":"{primer_headline}","body":"{primer_body}","image_url":"https://cdn.example.com/assets/push-primer-illustration.png","buttons":[{"text":"{primer_cta}","action":"request_push_permission"},{"text":"Not Now","action":"none"}],"close_button_color":"#999999"}}',
          },
        ],
        [
          {
            name: "dispatch_id",
            customName: "primer_dispatch_id",
            type: "string",
          },
          {
            name: "message",
            customName: "primer_display_status",
            type: "string",
          },
        ],
      ),
      n("6", "delayAction", "Wait 24 hours for permission decision", 1240, {
        delayDetails: { duration: 24, unit: "hours" },
      }),
      n("7", "condition", "Push permission still not granted?", 1480, {
        conditionDetails: {
          conditions: [
            { field: "existing_push_tokens", operator: "equals", value: "[]" },
          ],
        },
      }),
      emailNode(
        "8",
        "Braze: Send email explaining push notification benefits",
        1720,
        "{user_email}",
        "{fallback_email_subject}",
        "Hi {first_name},\n\nWelcome to the app! We want to make sure you never miss anything important.\n\nEnabling push notifications gives you:\n\n{value_propositions}\n\nYou're in full control — you can customize exactly which notifications you receive and turn them off anytime from your settings.\n\nTo enable notifications, open the app and tap the bell icon, or go to:\n[Enable Notifications](https://app.example.com/settings/notifications)\n\nYour experience, your way.\n\nThe Team",
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5", "output_1"),
      e("4", "6", "output_2"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8", "output_1"),
    ],
    summary:
      "Triggers on a user's first app open to initiate a soft push permission flow. AI crafts contextual primer messaging based on the user's signup source. If push tokens are absent, Braze displays an in-app modal explaining notification value before requesting system permission. After 24 hours, users who declined or missed the prompt receive a follow-up email explaining push benefits with a direct settings link.",
  },

  // ── 237. In-App to Email Fallback Journey | Pattern A ──
  "In-App to Email Fallback Journey": {
    nodes: [
      trigger("Braze: In-app message not seen webhook", "webhook_app", {
        source: "braze",
        event: "in_app_message.not_viewed",
        howItStarts:
          "Fires when a Braze in-app message campaign has been live for 48 hours and the targeted user has not opened the app to see the message, triggering an email fallback with the same content.",
        setup:
          "Configure Braze canvas with an in-app message step and a 48-hour expiry window. Set up a webhook trigger when the message expires without an impression event.",
        inputFields: [],
      }),
      aiNode(
        "2",
        "OpenAI: Adapt in-app content to email format",
        280,
        "You are a cross-channel content adapter. Convert the in-app message content into an engaging email format while preserving the core message and call to action. Return JSON with original_campaign_name, original_message_summary, user_email, user_name, email_subject (adapted from in-app headline), email_body_html (rich email version of the in-app content), primary_cta_text, primary_cta_url, channel_preference_recommendation (email_preferred, push_preferred, or balanced), and optimization_note.",
        "In-app message not viewed event: {trigger_body}",
        [
          {
            field: "original_campaign_name",
            as: "original_campaign_name",
            type: "string",
          },
          {
            field: "original_message_summary",
            as: "original_message_summary",
            type: "string",
          },
          { field: "user_email", as: "user_email", type: "string" },
          { field: "user_name", as: "user_name", type: "string" },
          { field: "email_subject", as: "email_subject", type: "string" },
          { field: "email_body_html", as: "email_body_html", type: "string" },
          { field: "primary_cta_text", as: "primary_cta_text", type: "string" },
          { field: "primary_cta_url", as: "primary_cta_url", type: "string" },
          {
            field: "channel_preference_recommendation",
            as: "channel_preference",
            type: "string",
          },
        ],
      ),
      brazeApiNode(
        "3",
        "Braze: Lookup user channel preferences and engagement",
        520,
        "/users/export/ids",
        [
          { key: "email_address", value: "{user_email}" },
          {
            key: "fields_to_export",
            value:
              '["external_id","email","first_name","email_subscribe","push_subscribe","custom_attributes"]',
          },
        ],
        [
          {
            name: "users[0].external_id",
            customName: "braze_user_id",
            type: "string",
          },
          {
            name: "users[0].email_subscribe",
            customName: "email_subscribed",
            type: "string",
          },
          {
            name: "users[0].push_subscribe",
            customName: "push_subscribed",
            type: "string",
          },
          {
            name: "users[0].custom_attributes.preferred_channel",
            customName: "preferred_channel",
            type: "string",
          },
        ],
      ),
      n("4", "condition", "User subscribed to email?", 760, {
        conditionDetails: {
          conditions: [
            {
              field: "email_subscribed",
              operator: "equals",
              value: "subscribed",
            },
          ],
        },
      }),
      emailNode(
        "5",
        "Braze: Send email fallback with adapted content",
        1000,
        "{user_email}",
        "{email_subject}",
        "Hi {user_name},\n\nWe had something to share with you in the app, but we noticed you haven't been around lately. Here it is via email instead:\n\n---\n\n{email_body_html}\n\n---\n\n[{primary_cta_text}]({primary_cta_url})\n\nPrefer to get updates in a different way? Update your notification preferences:\n[Manage Preferences](https://app.example.com/settings/notifications)\n\nThe Team",
      ),
      n("6", "delayAction", "Wait 48 hours for engagement", 1240, {
        delayDetails: { duration: 48, unit: "hours" },
      }),
      n("7", "condition", "Email not opened or clicked?", 1480, {
        conditionDetails: {
          conditions: [
            {
              field: "channel_preference",
              operator: "equals",
              value: "push_preferred",
            },
          ],
        },
      }),
      sheetsNode(
        "8",
        "Google Sheets: Log channel optimization data",
        1720,
        "ChannelOptimization",
        '[["{user_email}","{original_campaign_name}","in_app_not_viewed","email_fallback_sent","{channel_preference}","{preferred_channel}","=NOW()"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5", "output_1"),
      e("4", "6", "output_2"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8", "output_1"),
    ],
    summary:
      "Activates when a Braze in-app message expires unseen after 48 hours. AI adapts the in-app content to email format while preserving the CTA. If the user is email-subscribed, a fallback email delivers the adapted content. After another 48 hours, engagement data and channel preference signals are logged to Google Sheets to optimize future channel selection and reduce message loss.",
  },

  // ── 238. Content Recommendation Journey | Pattern I ──
  "Content Recommendation Journey": {
    nodes: [
      trigger("Braze: Content consumed webhook", "webhook_app", {
        source: "braze",
        event: "content.consumed",
        howItStarts:
          "Fires when a user finishes reading an article, watching a video, or completing a content piece, capturing consumption metadata for AI-powered next-content recommendations.",
        setup:
          "Configure Braze custom events for content consumption milestones (article_read, video_completed, guide_finished). Include content_id, content_type, category, and engagement_time in the payload.",
        inputFields: [],
      }),
      aiNode(
        "2",
        "OpenAI: Analyze content affinity and generate recommendations",
        280,
        "You are a content recommendation engine. Analyze the consumed content and the user's history to recommend 3 personalized next-content pieces. Return JSON with consumed_content_title, consumed_content_type, consumed_category, user_interest_profile (comma-separated topic affinities), recommendations (array of 3 objects with title, type, reason, url), recommendation_headline, progressive_cta (next action: deeper content, webinar, case study), and engagement_score (1-100 based on consumption depth).",
        "Content consumed event: {trigger_body}",
        [
          {
            field: "consumed_content_title",
            as: "consumed_content_title",
            type: "string",
          },
          {
            field: "consumed_category",
            as: "consumed_category",
            type: "string",
          },
          {
            field: "user_interest_profile",
            as: "user_interest_profile",
            type: "string",
          },
          {
            field: "recommendation_headline",
            as: "recommendation_headline",
            type: "string",
          },
          { field: "progressive_cta", as: "progressive_cta", type: "string" },
          { field: "engagement_score", as: "engagement_score", type: "number" },
        ],
      ),
      brazeApiNode(
        "3",
        "Braze: Fetch user lifecycle stage and content history",
        520,
        "/users/export/ids",
        [
          { key: "email_address", value: "{user_email}" },
          {
            key: "fields_to_export",
            value:
              '["external_id","email","first_name","custom_attributes","custom_events"]',
          },
        ],
        [
          {
            name: "users[0].external_id",
            customName: "braze_user_id",
            type: "string",
          },
          { name: "users[0].email", customName: "user_email", type: "string" },
          {
            name: "users[0].first_name",
            customName: "first_name",
            type: "string",
          },
          {
            name: "users[0].custom_attributes.lifecycle_stage",
            customName: "lifecycle_stage",
            type: "string",
          },
          {
            name: "users[0].custom_attributes.content_pieces_consumed",
            customName: "content_consumed_count",
            type: "number",
          },
        ],
      ),
      n("4", "condition", "Highly engaged content consumer?", 760, {
        conditionDetails: {
          conditions: [
            {
              field: "engagement_score",
              operator: "greater_than",
              value: "70",
            },
          ],
        },
      }),
      brazeApiNode(
        "5",
        "Braze: Send content card with personalized recommendations",
        1000,
        "/messages/send",
        [
          { key: "external_user_ids", value: '["{braze_user_id}"]' },
          {
            key: "messages",
            value:
              '{"content_card":{"title":"{recommendation_headline}","description":"Based on your interest in {consumed_category}, we picked these for you. Tap to explore.","url":"https://app.example.com/content/recommended?topic={consumed_category}&user={braze_user_id}"},"apple_push":{"alert":"New recommendations based on \\"{consumed_content_title}\\" — check them out!","extra":{"action":"content_recommendations","category":"{consumed_category}"}},"android_push":{"alert":"New recommendations based on \\"{consumed_content_title}\\" — check them out!","extra":{"action":"content_recommendations","category":"{consumed_category}"}}}',
          },
        ],
        [
          {
            name: "dispatch_id",
            customName: "reco_dispatch_id",
            type: "string",
          },
          {
            name: "message",
            customName: "reco_delivery_status",
            type: "string",
          },
        ],
      ),
      n("6", "delayAction", "Wait 4 days for content engagement", 1240, {
        delayDetails: { duration: 4, unit: "days" },
      }),
      n("7", "condition", "User consumed recommended content?", 1480, {
        conditionDetails: {
          conditions: [
            {
              field: "content_consumed_count",
              operator: "greater_than",
              value: "3",
            },
          ],
        },
      }),
      emailNode(
        "8",
        "Braze: Send progressive content email with deeper resources",
        1720,
        "{user_email}",
        "Your {consumed_category} deep dive continues — curated just for you",
        "Hi {first_name},\n\nYou've been exploring {consumed_category} content and we love your curiosity! With {content_consumed_count} pieces consumed, you're becoming a true expert.\n\nHere's your next level of learning:\n\n**{progressive_cta}**\n\nWe've curated deeper resources based on your interests in {user_interest_profile}:\n\n1. Advanced guide on {consumed_category} strategies\n2. Expert webinar recording with industry leaders\n3. Case study: How teams achieved 3x results\n\n[Access Your Curated Library](https://app.example.com/content/library?interests={user_interest_profile})\n\nReady to turn knowledge into action? [Book a Strategy Session](https://app.example.com/support/schedule)\n\nKeep learning!\nThe Team",
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5", "output_1"),
      e("4", "6", "output_2"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8", "output_1"),
    ],
    summary:
      "Triggers when a user finishes consuming content in the app. AI analyzes the content topic and user interest profile to generate 3 personalized recommendations. Highly engaged users receive Braze content cards and push notifications with curated picks. After 4 days, users who continue consuming content receive a progressive email with deeper resources like webinars, case studies, and strategy session invitations.",
  },
};
